// useFirebase.js
import { useState, useEffect } from 'react';
import { addDoc, updateDoc, getDoc, doc, onSnapshot, setDoc, writeBatch } from 'firebase/firestore';
import { collection as firestoreCollection } from 'firebase/firestore';
import { arrayUnion, arrayRemove } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../services/firebase';
import { initialQuadrantColors } from '../components/quadrantInfo';
import { noteCategories } from '../components/noteCategories';

const COLLECTIONS = {
  ACTIVE: 'notes',
  SCHEDULED: 'scheduled',
  ARCHIVED: 'archives',
  RECURRING: 'recurring'
};

const getCollectionForTaskType = (type) => {
  switch (type) {
    case 'scheduled':
      return COLLECTIONS.SCHEDULED;
    case 'recurring':
      return COLLECTIONS.RECURRING;
    case 'active':
    default:
      return COLLECTIONS.ACTIVE;
  }
};

const useFirebase = () => {
  // Existing state
  const [noteOrder, setNoteOrder] = useState([]); // Array of {id, type} objects
  const [notes, setNotes] = useState([]);
  const [scheduledTasks, setScheduledTasks] = useState([]);
  const [recurringTasks, setRecurringTasks] = useState([]);
  const [quadrantColors, setQuadrantColors] = useState(initialQuadrantColors);
  const [userPreferences, setUserPreferences] = useState({
    darkMode: false,
    gradientFrom: '#3B82F6',
    gradientTo: '#8B5CF6',
    language: 'en',
    notifications: true,
    defaultNoteColor: 'bg-purple-200',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    categoryContexts: noteCategories.reduce((acc, category) => {
      acc[category.name] = '';
      return acc;
    }, {})
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);

  // Auth listener
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribeAuth();
  }, []);

  // Reset state when user logs out
  useEffect(() => {
    if (!user) {
      setNotes([]);
      setNoteOrder([]);
      setScheduledTasks([]);
      setRecurringTasks([]);
      setQuadrantColors(initialQuadrantColors);
      setUserPreferences({
        darkMode: false,
        gradientFrom: '#3B82F6',
        gradientTo: '#8B5CF6',
        language: 'en',
        notifications: true,
        defaultNoteColor: 'bg-purple-200',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });
      return;
    }

    const fetchData = async () => {
      // Subscribe to active notes (existing functionality)
      try {
        // Subscribe to active notes
        const unsubscribeNotes = onSnapshot(
          firestoreCollection(db, 'users', user.uid, COLLECTIONS.ACTIVE),
          (snapshot) => {
            const fetchedNotes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setNotes(fetchedNotes);
          }
        );

        // Subscribe to scheduled tasks
        const unsubscribeScheduled = onSnapshot(
          firestoreCollection(db, 'users', user.uid, COLLECTIONS.SCHEDULED),
          (snapshot) => {
            const fetchedTasks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setScheduledTasks(fetchedTasks);
          }
        );

        // Subscribe to recurring tasks
        const unsubscribeRecurring = onSnapshot(
          firestoreCollection(db, 'users', user.uid, COLLECTIONS.RECURRING),
          (snapshot) => {
            const fetchedTasks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setRecurringTasks(fetchedTasks);
          }
        );

        // Subscribe to note order
        const unsubscribeOrder = onSnapshot(
          doc(db, 'users', user.uid, 'settings', 'noteOrder'),
          async (doc) => {
            if (doc.exists()) {
              setNoteOrder(doc.data().order);
            } else {
              // Initialize with existing notes if no order exists
              const initialOrder = [
                ...notes.map(note => ({ id: note.id, type: 'active' })),
                ...scheduledTasks.map(task => ({ id: task.id, type: 'scheduled' })),
                ...recurringTasks.map(task => ({ id: task.id, type: 'recurring' }))
              ];
              await setDoc(doc.ref, { order: initialOrder });
              setNoteOrder(initialOrder);
            }
          }
        );

        // Existing settings listeners
        const unsubscribeColors = onSnapshot(
          doc(db, 'users', user.uid, 'settings', 'quadrantColors'),
          async (doc) => {
            if (doc.exists()) {
              setQuadrantColors(doc.data().colors);
            } else {
              await setDoc(doc.ref, { colors: initialQuadrantColors });
            }
          }
        );

        const unsubscribePreferences = onSnapshot(
          doc(db, 'users', user.uid, 'settings', 'userPreferences'),
          async (doc) => {
            if (doc.exists()) {
              setUserPreferences(doc.data());
            } else {
              await setDoc(doc.ref, userPreferences);
            }
          }
        );

        return () => {
          unsubscribeNotes();
          unsubscribeScheduled();
          unsubscribeRecurring();
          unsubscribeColors();
          unsubscribePreferences();
          unsubscribeOrder();
        };
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err);
      }
    };
    fetchData();
  }, [user]);

  // Helper function to filter and sort notes by type
  const getOrderedNotesByType = (type) => {
    const relevantNotes = (() => {
      switch (type) {
        case 'active': return notes;
        case 'scheduled': return scheduledTasks;
        case 'recurring': return recurringTasks;
        default: return [];
      }
    })();

    // Get the ordered IDs for this type
    const typeOrder = noteOrder
      .filter(item => item.type === type)
      .map(item => item.id);

    // Create a map for quick lookup
    const notesMap = new Map(relevantNotes.map(note => [note.id, note]));

    // Return notes in order, followed by any unordered notes
    const orderedNotes = typeOrder
      .filter(id => notesMap.has(id))
      .map(id => notesMap.get(id));

    // Add any notes that aren't in the order
    const unorderedNotes = relevantNotes.filter(note => !typeOrder.includes(note.id));

    return [...orderedNotes, ...unorderedNotes];
  };

  // Task lifecycle management functions
  const addTask = async (task, collectionName = COLLECTIONS.ACTIVE) => {
    if (!user) return;
    try {
      const docRef = await addDoc(firestoreCollection(db, 'users', user.uid, collectionName), {
        ...task,
        createdAt: new Date().toISOString()
      });

      // Get task type from collection name
      const type = Object.entries(COLLECTIONS).find(([, value]) => value === collectionName)?.[0].toLowerCase();

      if (type) {
        // Add new task to the beginning of the order
        const newOrder = [{ id: docRef.id, type }, ...noteOrder];
        await updateNoteOrder(newOrder);
      }

      return docRef.id;
    } catch (err) {
      console.error("Error adding task:", err);
      setError(err);
      return null;
    }
  };

  const moveTask = async (taskId, fromCollection, toCollection, updates = {}) => {
    if (!user) return;

    const batch = writeBatch(db);

    try {
      const sourceRef = doc(db, 'users', user.uid, fromCollection, taskId);
      const targetRef = doc(db, 'users', user.uid, toCollection, taskId);

      const taskDoc = await getDoc(sourceRef);
      if (!taskDoc.exists()) {
        throw new Error('Task not found');
      }

      // Copy to target collection with updates
      batch.set(targetRef, {
        ...taskDoc.data(),
        ...updates
      });

      batch.delete(sourceRef);

      // Update the task type in noteOrder
      const newType = Object.entries(COLLECTIONS)
        .find(([, value]) => value === toCollection)?.[0].toLowerCase();

      if (newType) {
        const newOrder = noteOrder.map(item =>
          item.id === taskId ? { ...item, type: newType } : item
        );
        batch.set(doc(db, 'users', user.uid, 'settings', 'noteOrder'), { order: newOrder });
      }

      await batch.commit();
    } catch (err) {
      console.error("Error moving task:", err);
      setError(err);
    }
  };

  const updateTask = async (id, updates, collectionName = COLLECTIONS.ACTIVE) => {
    if (!user) return;
    try {
      const processedUpdates = {};
      for (const [key, value] of Object.entries(updates)) {
        if (value && value.__type === 'arrayUnion') {
          processedUpdates[key] = arrayUnion(value.value);
        } else if (value && value.__type === 'arrayRemove') {
          processedUpdates[key] = arrayRemove(value.value);
        } else {
          processedUpdates[key] = value;
        }
      }

      await updateDoc(doc(db, 'users', user.uid, collectionName, id), processedUpdates);
    } catch (err) {
      console.error("Error updating task:", err);
      setError(err);
    }
  };

  // Archive instead of delete
  const archiveTask = async (id, fromCollection = COLLECTIONS.ACTIVE) => {
    await moveTask(id, fromCollection, COLLECTIONS.ARCHIVED, {
      archivedAt: new Date().toISOString(),
      status: 'archived'
    });
  };

  const updateQuadrantColors = async (colors) => {
    if (!user) return;
    try {
      await setDoc(doc(db, 'users', user.uid, 'settings', 'quadrantColors'), { colors });
    } catch (err) {
      console.error("Error updating quadrant colors:", err);
      setError(err);
    }
  };

  const updateUserPreferences = async (preferences) => {
    if (!user) return;
    try {
      await setDoc(doc(db, 'users', user.uid, 'settings', 'userPreferences'), preferences);
    } catch (err) {
      console.error("Error updating user preferences:", err);
      setError(err);
    }
  };

  const updateNoteOrder = async (newOrder) => {
    if (!user) return;
    try {
      await setDoc(doc(db, 'users', user.uid, 'settings', 'noteOrder'), { order: newOrder });
      setNoteOrder(newOrder);
    } catch (err) {
      console.error("Error updating note order:", err);
      setError(err);
    }
  };

  return {
    // Existing exports
    notes: getOrderedNotesByType('active'),
    scheduledTasks: getOrderedNotesByType('scheduled'),
    recurringTasks: getOrderedNotesByType('recurring'),
    noteOrder,
    quadrantColors,
    userPreferences,
    updateQuadrantColors,
    updateUserPreferences,
    updateNoteOrder,
    loading,
    error,
    user,
    addTask,
    updateTask,
    archiveTask,
    moveTask
  };
};

export { useFirebase, COLLECTIONS, getCollectionForTaskType };